// src/components/Universals/CustomerService/supportChatWindow.component.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import HelpQuestionPanel from "./helpQuestionPanel.component.jsx";

const SUBJECT_OPTIONS = [
    "Customer Support",
    "New Features (Coming soon)",
];

const SupportChatWindow = ({ currentUser, onClose }) => {
    const endpoint = "https://www.fbxchange.com/ReactPHP/supportChat.php";

    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [creatingThread, setCreatingThread] = useState(false);
    const [checkingExisting, setCheckingExisting] = useState(true);
    const [endingChat, setEndingChat] = useState(false);
    const [activeMode, setActiveMode] = useState("common");
    const messagesWrapRef = useRef(null);
    const shouldAutoScrollMessagesRef = useRef(true);

    const userID = Number(currentUser?.scout ?? 0);

    const firstName = String(currentUser?.firstName ?? "").trim();
    const lastName = String(currentUser?.lastName ?? "").trim();

    const userName =
        `${firstName}${firstName && lastName ? " " : ""}${lastName}`.trim() ||
        currentUser?.userName ||
        currentUser?.name ||
        currentUser?.displayName ||
        "User";

    const displayName =
        String(currentUser?.displayName ?? "").trim() ||
        `${firstName}${firstName && lastName ? " " : ""}${lastName}`.trim() ||
        currentUser?.userName ||
        currentUser?.name ||
        "User";

    const phoneNumber =
        String(
            currentUser?.phone ||
            currentUser?.phoneNumber ||
            currentUser?.telephone ||
            currentUser?.cell ||
            ""
        ).trim();

    const userSecurity = String(currentUser?.userContactLevel ?? "");
    const userTeam = currentUser?.team || "";

    const loadExistingThread = useCallback(async () => {
        if (!userID) {
            setThread(null);
            setSubject("");
            setCheckingExisting(false);
            return null;
        }

        setCheckingExisting(true);

        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "getExistingSupportThread",
                    userID,
                }),
            });

            const data = await res.json();

            if (data?.success && data?.thread) {
                setThread(data.thread);
                setSubject(data.thread.subject || "");
                setActiveMode("support");
                return data.thread;
            }

            setThread(null);
            setSubject("");
            return null;
        } catch (err) {
            console.log("loadExistingThread error", err);
            setThread(null);
            setSubject("");
            return null;
        } finally {
            setCheckingExisting(false);
        }
    }, [endpoint, userID]);

    const createThread = useCallback(
        async (subjectIn) => {
            if (!userID || !subjectIn) return null;

            setSubject(subjectIn);

            try {
                const res = await fetch(endpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        scriptName: "createSupportThread",
                        userID,
                        userName,
                        displayName,
                        phoneNumber,
                        subject: subjectIn,
                        userSecurity,
                        userTeam,
                    }),
                });

                const data = await res.json();

                if (data?.success && data?.thread) {
                    setThread(data.thread);
                    setSubject(data.thread.subject || subjectIn);
                    return data.thread;
                }
            } catch (err) {
                console.log("createThread error", err);
            }

            return null;
        },
        [
            endpoint,
            userID,
            userName,
            displayName,
            phoneNumber,
            userSecurity,
            userTeam,
        ]
    );

    const loadMessages = useCallback(
        async (threadID) => {
            if (!threadID || !userID) return;

            try {
                const res = await fetch(endpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        scriptName: "getThreadMessages",
                        threadID,
                        viewerType: "user",
                        viewerUserID: userID,
                    }),
                });

                const data = await res.json();
                setMessages(Array.isArray(data?.messages) ? data.messages : []);
            } catch (err) {
                console.log("loadMessages error", err);
                setMessages([]);
            }
        },
        [endpoint, userID]
    );

    const markMessagesForBottomScroll = () => {
        shouldAutoScrollMessagesRef.current = true;
    };

    const handleMessagesScroll = () => {
        const messagesWrap = messagesWrapRef.current;
        if (!messagesWrap) return;

        const distanceFromBottom =
            messagesWrap.scrollHeight - messagesWrap.scrollTop - messagesWrap.clientHeight;
        shouldAutoScrollMessagesRef.current = distanceFromBottom < 40;
    };

    const handleChooseOption = async (option) => {
        if (creatingThread) return;

        // Only Tech Support / Customer Support should do anything
        if (option !== "Customer Support") {
            return;
        }

        setCreatingThread(true);
        try {
            const newThread = await createThread(option);
            if (newThread?.id) {
                setActiveMode("support");
                markMessagesForBottomScroll();
                await loadMessages(newThread.id);
            }
        } finally {
            setCreatingThread(false);
        }
    };

    const startCustomerSupport = async () => {
        setActiveMode("support");
        if (thread?.id || creatingThread) return;
        await handleChooseOption("Customer Support");
    };

    const sendMessage = async () => {
        if (!thread?.id || !message.trim() || loading) return;

        setLoading(true);
        markMessagesForBottomScroll();

        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "sendSupportMessage",
                    threadID: thread.id,
                    senderUserID: userID,
                    senderName: displayName,
                    senderType: "user",
                    senderSecurity: userSecurity,
                    senderTeam: userTeam,
                    message: message.trim(),
                }),
            });

            const data = await res.json();

            if (data?.success) {
                setMessage("");
                await loadMessages(thread.id);
            }
        } catch (err) {
            console.log("sendMessage error", err);
        } finally {
            setLoading(false);
        }
    };

    const closeThread = async () => {
        if (!thread?.id) return false;

        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    scriptName: "closeSupportThread",
                    threadID: thread.id,
                }),
            });

            const data = await res.json();
            return !!data?.success;
        } catch (err) {
            console.log("closeThread error", err);
            return false;
        }
    };

    const handleCloseChat = async () => {
        if (!thread?.id) {
            onClose();
            return;
        }

        const confirmed = window.confirm(
            "Are you sure you want to close this chat?\n\nThis will mark the support ticket as not open."
        );

        if (!confirmed) return;

        setEndingChat(true);
        try {
            const ok = await closeThread();
            if (ok) {
                onClose();
            }
        } finally {
            setEndingChat(false);
        }
    };

    const handleMinimize = () => {
        onClose();
    };

    const handleMessageKeyDown = async (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!loading && message.trim()) {
                await sendMessage();
            }
        }
    };

    const formatMessageTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(String(dateString).replace(" ", "T"));
        if (Number.isNaN(date.getTime())) return dateString;

        return date.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };

    useEffect(() => {
        loadExistingThread();
    }, [loadExistingThread]);

    useEffect(() => {
        if (!thread?.id) return;

        loadMessages(thread.id);

        const interval = setInterval(() => {
            loadMessages(thread.id);
        }, 4000);

        return () => clearInterval(interval);
    }, [thread?.id, loadMessages]);

    useEffect(() => {
        const messagesWrap = messagesWrapRef.current;
        if (!messagesWrap || !shouldAutoScrollMessagesRef.current) return;

        requestAnimationFrame(() => {
            messagesWrap.scrollTop = messagesWrap.scrollHeight;
        });
    }, [messages]);

    const showChooser = !checkingExisting && !thread?.id;
    const showCommonQuestions = activeMode === "common";

    return (
        <div
            className="card shadow position-fixed"
            style={{
                bottom: "92px",
                right: "18px",
                width: "560px",
                height: "560px",
                zIndex: 99999,
                borderRadius: "14px",
                overflow: "hidden",
            }}
        >
            <div className="card-header d-flex justify-content-between align-items-center bg-primary text-white mb-0">
                <strong>{showCommonQuestions ? "Common Questions" : "Customer Service"}</strong>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={handleMinimize}
                    title="Minimize"
                >
                    -
                </button>
            </div>

            <div
                className="card-body d-flex flex-column p-2"
                style={{ minHeight: 0 }}
            >
                <div className="d-flex gap-2 mb-2">
                    <button
                        type="button"
                        className={`btn btn-sm w-50 ${activeMode === "common" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setActiveMode("common")}
                    >
                        Common Questions
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm w-50 ${activeMode === "support" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={startCustomerSupport}
                        disabled={checkingExisting || creatingThread}
                    >
                        {creatingThread ? "Starting..." : "Customer Support"}
                    </button>
                </div>

                {showCommonQuestions ? (
                    <HelpQuestionPanel currentUser={currentUser} />
                ) : checkingExisting ? (
                    <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted">
                        Checking for existing support session...
                    </div>
                ) : showChooser ? (
                    <div className="d-flex flex-column justify-content-center h-100">
                        <div className="text-center mb-4">
                            <h5 className="mb-2">Which of these items would you like?</h5>
                            <div className="text-muted small">
                                Choose one option to start your support session.
                            </div>
                        </div>

                        <div className="row g-2">
                            {SUBJECT_OPTIONS.map((option) => {
                                const isDisabled = option !== "Customer Support";

                                return (
                                    <div key={option} className="col-48">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary w-100"
                                            onClick={() => handleChooseOption(option)}
                                            disabled={creatingThread || isDisabled}
                                            style={{ height: "52px" }}
                                        >
                                            {creatingThread && subject === option
                                                ? "Starting..."
                                                : option}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="text-center text-muted small mt-3">
                            Your account info will be used for this request.
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="border rounded p-2 mb-2 bg-light">
                            <div className="fw-bold">
                                {thread?.displayName || displayName}
                            </div>

                            <div className="small text-muted">
                                Username: {thread?.userName || userName}
                            </div>

                            <div className="small text-muted">
                                Phone: {thread?.phoneNumber || phoneNumber || "No phone on file"}
                            </div>

                            <div className="small text-muted">
                                Topic: {thread?.subject || subject || "Support"}
                            </div>
                        </div>

                        <div
                            ref={messagesWrapRef}
                            className="flex-grow-1 border rounded p-2 mb-2 bg-light"
                            onScroll={handleMessagesScroll}
                            style={{ overflowY: "auto", minHeight: 0 }}
                        >
                            {messages.length === 0 ? (
                                <div className="text-muted small">No messages yet.</div>
                            ) : (
                                messages.map((m) => (
                                    <div
                                        key={m.id}
                                        className={`mb-2 p-2 rounded ${m.senderType === "admin"
                                            ? "alert alert-warning"
                                            : "alert alert-primary"
                                            }`}
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <div className="small fw-bold">
                                                {m.senderType === "admin"
                                                    ? `Support: ${m.senderName || ""}`
                                                    : `${m.senderName || displayName}`}
                                            </div>
                                            <div className="text-muted small">
                                                {formatMessageTime(m.createdAt)}
                                            </div>
                                        </div>

                                        <div
                                            className="small"
                                            dangerouslySetInnerHTML={{
                                                __html: String(m.message || "").replace(/\n/g, "<br/>"),
                                            }}
                                        />
                                    </div>
                                ))
                            )}
                        </div>

                        <textarea
                            className="form-control mb-2"
                            rows="4"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleMessageKeyDown}
                            placeholder="Type your message... Press Enter to send, Shift+Enter for a new line."
                            disabled={loading || endingChat}
                        />

                        <div className="d-flex justify-content-between">
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleMinimize}
                                    disabled={endingChat}
                                >
                                    Minimize
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={handleCloseChat}
                                    disabled={endingChat}
                                >
                                    {endingChat ? "Closing..." : "Close Chat"}
                                </button>
                            </div>

                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={loading || !message.trim() || endingChat}
                                onClick={sendMessage}
                            >
                                {loading ? "Sending..." : "Send"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SupportChatWindow;
